import { useEffect, useRef, type RefObject } from 'react';

import { displayScale } from '../../player/peaks';

/**
 * The bars on the screen, moving to the record rather than to a timer.
 *
 * ## The loop was always a placeholder
 *
 * `.eq` has a CSS keyframe on it and always has - five bars breathing on a
 * 0.9s cycle, which is a screensaver. It is on a stage above a room where a
 * specific piece of music is playing at a specific moment, and it was hitting
 * the beat by coincidence. You can always tell.
 *
 * The peaks file the transcoder already writes (§4.3) is 1800 true amplitudes
 * per track, already on disk and already fetched by the scrubber. That is the
 * same source `Lights` uses for the player's light rig, for the same reason
 * given there: a real `AnalyserNode` needs CORS-clean audio, and ours is HLS
 * behind a signed cookie.
 *
 * ## It works whether or not you are listening
 *
 * Deliberately off the room's own clock - `positionNow` - rather than off an
 * audio element. Half the people looking at a stage have not tuned in yet, and
 * the room knows exactly where it is in the track either way. Somebody who
 * pressed listen and somebody who is only looking see the same bars doing the
 * same thing at the same moment, which is what makes it *the room's* screen.
 *
 * ## Five bars from one envelope
 *
 * There is no spectrum here, so the honest thing to do with five bars is not to
 * pretend they are frequency bands. They are the same level read at slightly
 * different moments - the centre bar is now, the pair beside it 70ms behind,
 * the outer pair 140 - so a hit arrives in the middle and spreads outwards.
 * That reads as a room responding to a sound, which is what it is, and it never
 * claims to be an analysis it did not do.
 *
 * ## Custom properties on one element, not sixty writes a second through React
 *
 * The same trick `Lights` uses. Five properties on the stage's root, and CSS
 * hands each bar its own - so a frame costs five style writes rather than a
 * re-render of three hundred SVG nodes.
 */

/** How far behind now each bar reads, in milliseconds. Centre out. */
const LAG = [140, 70, 0, 70, 140];

/** The floor a bar sits at when there is silence, and its full height. */
const FLOOR = 0.16;

export interface EqSource {
  /** The track's amplitudes, or null while there is nothing to read. */
  peaks: number[] | null;
  /** Milliseconds. Without it there is no way to map a moment to a peak. */
  duration: number;
  /**
   * Where the room is now, in milliseconds, asked once a frame.
   *
   * A function rather than a number because this runs at 60fps and the value
   * changes every frame - passing it as a prop would mean a render per frame to
   * move five rectangles.
   */
  at: () => number;
  /**
   * Whether the room is actually playing it.
   *
   * A paused room settles its bars on the floor and stops the loop, rather than
   * holding them at whatever level the track was at when the host stepped away.
   * Handing the CSS loop back would be worse than either: bars dancing over a
   * room that has gone quiet.
   */
  playing: boolean;
}

/**
 * Drive the bars inside `node`, and say whether anything is driving them.
 *
 * `false` means the caller should leave the CSS loop alone: there is no track,
 * no peaks file, or nothing is playing. That is the honest fallback - bars that
 * stood still whenever a peaks file was missing would read as a broken screen
 * rather than as a stage with the lights on.
 */
export function useStageEq(node: RefObject<HTMLElement | null>, source: EqSource | null): boolean {
  /*
   * The clock, held in a ref so the frame loop can read the newest one without
   * the effect restarting every time the room polls.
   */
  const at = useRef(source?.at);

  at.current = source?.at;

  const peaks = source?.peaks ?? null;
  const duration = source?.duration ?? 0;
  const playing = source?.playing ?? false;
  const driving = peaks !== null && peaks.length > 0 && duration > 0;

  useEffect(() => {
    const element = node.current;

    if (!element || !driving || peaks === null) return;

    const scale = displayScale(peaks);
    const bars = LAG.length;

    /** Where each bar is now, so it can be moved towards where it should be. */
    const level: number[] = Array.from({ length: bars }, () => FLOOR);

    const write = (i: number, value: number) => {
      element.style.setProperty(`--eq-${i + 1}`, value.toFixed(3));
    };

    /*
     * Somebody who asked not to be moved is not moved, and is not charged for a
     * frame loop either. The bars stand at full height, which is what the
     * reduced-motion rule in `stage.css` draws anyway.
     */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      for (let i = 0; i < bars; i++) write(i, 1);

      return;
    }

    /*
     * Nothing playing, so nothing moving - and no frame loop for it. A room can
     * sit paused for an hour, and sixty writes a second of the same five
     * numbers is an hour of compositing for a still picture.
     */
    if (!playing) {
      for (let i = 0; i < bars; i++) write(i, FLOOR);

      return;
    }

    let frame = 0;

    const draw = () => {
      const now = at.current?.() ?? 0;

      for (let i = 0; i < bars; i++) {
        const moment = Math.max(0, now - (LAG[i] ?? 0));
        const played = Math.min(1, moment / duration);
        const loud = Math.min(1, sample(peaks, played * (peaks.length - 1)) * scale);

        /*
         * Fast up, slow down. A bar that rises as slowly as it falls turns
         * every transient into a swell, which is the opposite of what a level
         * meter is for.
         */
        const was = level[i] ?? FLOOR;

        level[i] = was + (loud - was) * (loud > was ? 0.45 : 0.12);

        write(i, FLOOR + (1 - FLOOR) * (level[i] ?? 0));
      }

      frame = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(frame);

      // Back to the CSS loop's own scale, so a stopped track does not leave
      // five bars frozen at whatever height the last frame drew.
      for (let i = 0; i < bars; i++) element.style.removeProperty(`--eq-${i + 1}`);
    };
  }, [node, peaks, duration, playing, driving]);

  return driving;
}

/**
 * An array read at a fractional index.
 *
 * Interpolated rather than rounded, for the reason `Lights` gives: the file is
 * 1800 points however long the track is, so rounding makes a short song step
 * nine times a second and an hour-long mix step twice a minute.
 */
function sample(values: number[], index: number): number {
  if (values.length === 0 || index < 0) return 0;

  const low = Math.min(values.length - 1, Math.floor(index));
  const high = Math.min(values.length - 1, low + 1);
  const mix = index - low;

  return (values[low] ?? 0) * (1 - mix) + (values[high] ?? 0) * mix;
}
